import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useServicesContent } from "@/hooks/useServicesContent";
import { Loader2, Save, Trash2 } from "lucide-react";

export function ServicesManager() {
  const { content, loading, updateContent, deleteContent } = useServicesContent();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const heroContent = content.find((c) => c.section === "hero");
  const mainServices = content.filter((c) => c.section === "main_services");
  const additionalServices = content.filter((c) => c.section === "additional_services");
  const ctaContent = content.find((c) => c.section === "cta");

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setEditForm({
      ...item.content_value,
      is_active: item.is_active,
    });
  };

  const handleSave = async (id: string) => {
    const item = content.find((c) => c.id === id);
    if (!item) return;

    const { is_active, ...contentValue } = editForm;
    await updateContent(id, {
      content_value: contentValue,
      is_active,
    });
    setEditingId(null);
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    await updateContent(id, { is_active: !currentActive });
  };

  const renderHeroEditor = () => {
    if (!heroContent) return null;

    const isEditing = editingId === heroContent.id;
    const data = isEditing ? editForm : heroContent.content_value;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Hero Section</CardTitle>
              <CardDescription>Título e subtítulo da página</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={heroContent.is_active ? "default" : "secondary"}>
                {heroContent.is_active ? "Ativo" : "Inativo"}
              </Badge>
              <Switch
                checked={heroContent.is_active ?? true}
                onCheckedChange={() => handleToggleActive(heroContent.id, heroContent.is_active ?? true)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input
              value={data.title || ""}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              onFocus={() => handleEdit(heroContent)}
              maxLength={200}
            />
          </div>
          <div>
            <Label>Subtítulo</Label>
            <Textarea
              value={data.subtitle || ""}
              onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
              onFocus={() => handleEdit(heroContent)}
              maxLength={500}
              rows={3}
            />
          </div>
          {isEditing && (
            <Button onClick={() => handleSave(heroContent.id)}>
              <Save className="w-4 h-4 mr-2" />
              Salvar Alterações
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderServiceCard = (service: any) => {
    const isEditing = editingId === service.id;
    const data = isEditing ? editForm : service.content_value;

    return (
      <Card key={service.id}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{data.title}</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={service.is_active ? "default" : "secondary"}>
                {service.is_active ? "Ativo" : "Inativo"}
              </Badge>
              <Switch
                checked={service.is_active ?? true}
                onCheckedChange={() => handleToggleActive(service.id, service.is_active ?? true)}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                    <AlertDialogDescription>
                      Tem certeza que deseja excluir este serviço? Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteContent(service.id)}>
                      Excluir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input
              value={data.title || ""}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              onFocus={() => handleEdit(service)}
              maxLength={200}
            />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea
              value={data.description || ""}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              onFocus={() => handleEdit(service)}
              maxLength={500}
              rows={3}
            />
          </div>
          {data.features && (
            <div>
              <Label>Características (uma por linha)</Label>
              <Textarea
                value={data.features?.join("\n") || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, features: e.target.value.split("\n") })
                }
                onFocus={() => handleEdit(service)}
                rows={5}
              />
            </div>
          )}
          <div>
            <Label>Texto do Botão</Label>
            <Input
              value={data.buttonText || ""}
              onChange={(e) => setEditForm({ ...editForm, buttonText: e.target.value })}
              onFocus={() => handleEdit(service)}
              maxLength={50}
            />
          </div>
          {isEditing && (
            <Button onClick={() => handleSave(service.id)}>
              <Save className="w-4 h-4 mr-2" />
              Salvar Alterações
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderCTAEditor = () => {
    if (!ctaContent) return null;

    const isEditing = editingId === ctaContent.id;
    const data = isEditing ? editForm : ctaContent.content_value;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Call to Action</CardTitle>
              <CardDescription>Seção de chamada para ação</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={ctaContent.is_active ? "default" : "secondary"}>
                {ctaContent.is_active ? "Ativo" : "Inativo"}
              </Badge>
              <Switch
                checked={ctaContent.is_active ?? true}
                onCheckedChange={() => handleToggleActive(ctaContent.id, ctaContent.is_active ?? true)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input
              value={data.title || ""}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              onFocus={() => handleEdit(ctaContent)}
              maxLength={200}
            />
          </div>
          <div>
            <Label>Subtítulo</Label>
            <Textarea
              value={data.subtitle || ""}
              onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
              onFocus={() => handleEdit(ctaContent)}
              maxLength={500}
              rows={2}
            />
          </div>
          <div>
            <Label>Texto do Botão</Label>
            <Input
              value={data.buttonText || ""}
              onChange={(e) => setEditForm({ ...editForm, buttonText: e.target.value })}
              onFocus={() => handleEdit(ctaContent)}
              maxLength={50}
            />
          </div>
          {isEditing && (
            <Button onClick={() => handleSave(ctaContent.id)}>
              <Save className="w-4 h-4 mr-2" />
              Salvar Alterações
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="hero" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="hero">Hero</TabsTrigger>
          <TabsTrigger value="main">Serviços Principais</TabsTrigger>
          <TabsTrigger value="additional">Serviços Adicionais</TabsTrigger>
          <TabsTrigger value="cta">CTA</TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="space-y-4">
          {renderHeroEditor()}
        </TabsContent>

        <TabsContent value="main" className="space-y-4">
          <div className="grid gap-4">
            {mainServices.map((service) => renderServiceCard(service))}
          </div>
        </TabsContent>

        <TabsContent value="additional" className="space-y-4">
          <div className="grid gap-4">
            {additionalServices.map((service) => renderServiceCard(service))}
          </div>
        </TabsContent>

        <TabsContent value="cta" className="space-y-4">
          {renderCTAEditor()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
